import {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { ToastInstance } from "./ToastView.js";

/**
 * Sprint 17 Wave F — minimal toast component + provider.
 *
 * Renders a stacked column of dismissible notifications in the
 * lower-right of the viewport. Each toast carries an optional
 * `requestId` (sourced from `ApiError.requestId` / Sprint 16 Wave H)
 * so an operator can correlate a user-visible failure to the
 * structured access-log row.
 *
 * Behaviour:
 *   - role="status" + aria-live="polite" so screen readers
 *     announce the toast politely (not alert; the page is still
 *     usable underneath).
 *   - Dismissible via the close button.
 *   - Auto-dismiss after `durationMs` (default 8 s) for non-error
 *     tones; error toasts persist until dismissed.
 *   - Stacked vertically; max-width 420 px; never overlaps the
 *     content area.
 *   - Non-blocking: the page underneath remains interactive.
 *
 * Out of scope: animations, swipe-to-dismiss, action buttons
 * inside toasts. Sprint 18+ if needed.
 */

export type ToastTone = "info" | "success" | "warn" | "error";

export interface ToastInput {
  readonly id?: string;
  readonly tone: ToastTone;
  readonly title?: string;
  readonly message: string;
  /** Optional Sprint 16 Wave H request-id from an ApiError. */
  readonly requestId?: string | null | undefined;
  /** Override default auto-dismiss (8 s for non-error; never for error). */
  readonly durationMs?: number | null;
}

interface ToastContextValue {
  readonly push: (toast: ToastInput) => string;
  readonly dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let counter = 0;
const newId = () => `toast_${Date.now()}_${++counter}`;

/**
 * E-BUNDLE-21 — Ladeschranke vor dem Darstellungsteil.
 *
 * `ToastView` samt Farbtabelle wird erst gebraucht, wenn ein Toast gepusht
 * wurde; beim ersten Paint ist die Liste immer leer. Provider, Kontext und
 * `useToast()` bleiben deshalb statisch, das JSX laedt diese Schranke nach.
 */
const ToastView = lazy(async () => {
  const mod = await import("./ToastView.js");
  return { default: mod.ToastView };
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastInstance[]>([]);
  // Map id → setTimeout handle so we can cancel on early dismiss.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const handle = timers.current.get(id);
    if (handle) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: ToastInput): string => {
      const id = toast.id ?? newId();
      const instance: ToastInstance = {
        id,
        tone: toast.tone,
        title: toast.title ?? "",
        message: toast.message,
        requestId: toast.requestId ?? undefined,
        durationMs: toast.durationMs,
      };
      setItems((prev) => [...prev, instance]);
      // Auto-dismiss schedule.
      const explicit = toast.durationMs;
      if (explicit !== null) {
        const ms = typeof explicit === "number" ? explicit : toast.tone === "error" ? null : 8000;
        if (typeof ms === "number" && ms > 0) {
          const handle = setTimeout(() => dismiss(id), ms);
          timers.current.set(id, handle);
        }
      }
      return id;
    },
    [dismiss],
  );

  // Clean up timers on unmount.
  useEffect(() => {
    const t = timers.current;
    return () => {
      for (const handle of t.values()) clearTimeout(handle);
      t.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // Live region; toasts inside announce themselves.
        aria-live="polite"
        aria-relevant="additions text"
        style={{
          position: "fixed",
          insetBlockEnd: 16,
          insetInlineEnd: 16,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          maxInlineSize: 420,
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        <Suspense fallback={null}>
          {items.map((toast) => (
            <ToastView key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
          ))}
        </Suspense>
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Hook for pushing toasts. Throws if used outside <ToastProvider>;
 * this is intentional so a missing provider is a fail-fast in dev
 * rather than a silent no-op.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast() called outside <ToastProvider>");
  }
  return ctx;
}
